<template>
  <div class="stewards">
    <h4 class="zone zone--loud" data-tut="stewards-shifts">
      <span class="zone__label">Put your people to work</span>
      <span class="zone__count" :class="{ 'zone__count--spent': !civ.workers }">
        {{ civ.workers }} shift{{ civ.workers === 1 ? '' : 's' }} left
      </span>
    </h4>

    <CityScene
      data-tut="stewards-city"
      :city="cityScene"
      :labels="labels"
      :flash="flash"
      :wide="expanding"
      @pick="onPick"
    />
    <!-- The city's standing fact, in the same shape as the Frontier's
         movement speed: a small-caps label and the numbers as pips. It was
         a grey sentence, which is the one thing rule 3 forbids. -->
    <p class="fact">
      <span class="fact__label">Stores keep overnight</span>
      <span class="fact__bit"><Pip kind="food" :n="foodCap" /></span>
      <span class="fact__bit"><Pip kind="goods" :n="goodsCap" /></span>
      <span class="fact__bit">the rest spoils</span>
    </p>

    <!-- House style rule 4: ONE heading, ONE grid. The city's own jobs and
         the errands out of town used to be two grids under two headings;
         they are all just what a shift can do today. -->
    <h4 v-if="!pickingBuild" class="zone">
      <span class="zone__label">Things to do here</span>
    </h4>

    <ul v-if="!pickingBuild" class="hall__actions hall__actions--fit" data-tut="stewards-jobs">
      <li>
        <button class="tile" :class="{ 'tile--on': expanding }" :disabled="!canAct || !annexCheck.ok" @click="expanding = !expanding">
          <span class="tile__head">
            <!-- eslint-disable-next-line vue/no-v-html -- constant art from buildingArt.js -->
            <svg class="tile__icon" viewBox="0 0 24 24" aria-hidden="true" v-html="annexArt" />
            <span class="tile__name">{{ expanding ? 'Never mind' : 'Take in land' }}</span>
          </span>
          <ActionCost :cost="ANNEX_COST" as-shifts />
          <span class="tile__gain">{{ annexCheck.ok || annexCheck.kind === 'cost' ? (expanding ? 'tap a blue hex' : 'six more plots') : annexWhy }}</span>
        </button>
      </li>
      <li>
        <!-- Always opens, to LOOK (playtest 12, Matt: "I can't even open it
             to look at what I might wanna build"): at dawn, and when nothing
             is affordable. The buttons inside are what stay locked. -->
        <button class="tile" :class="{ 'tile--off': !canAct || !buildAnywhere }" data-tut="stewards-build" @click="pickingBuild = true">
          <span class="tile__head">
            <!-- eslint-disable-next-line vue/no-v-html -- constant art from buildingArt.js -->
            <svg class="tile__icon" viewBox="0 0 24 24" aria-hidden="true" v-html="buildArt" />
            <span class="tile__name">Build</span>
          </span>
          <ActionCost :cost="{ workers: 2 }" as-shifts />
          <!-- Room and cost are different reasons (playtest 2026-09-25: it said
               "no room left" when the city had nine free plots and the town
               was simply short of goods). -->
          <span class="tile__gain">{{ buildAnywhere ? 'plus what it is made of' : builtToday ? 'built today - one a day' : (roomAnywhere ? 'not enough to build anything yet' : 'no room left - take in land') }}</span>
        </button>
      </li>
      <!-- Caravans, buying grain and haggling happen off the map, but they
           are still a shift spent, so they sit in the same grid. -->
      <li v-for="a in buttons" :key="a.id">
        <button
          class="tile"
          :class="{ 'tile--off': !a.check.ok }"
          :disabled="!canAct || !a.check.ok"
          :title="a.check.ok ? null : a.check.why"
          @click="tap(a)"
        >
          <span class="tile__head">
            <!-- eslint-disable-next-line vue/no-v-html -- constant art from buildingArt.js -->
            <svg class="tile__icon" viewBox="0 0 24 24" aria-hidden="true" v-html="a.art" />
            <span class="tile__name">{{ a.label }}</span>
          </span>
          <ActionCost :cost="a.cost" as-shifts />
          <span class="tile__gain">{{ a.check.ok || a.check.kind === 'cost' ? a.gain : a.check.why }}</span>
        </button>
      </li>
    </ul>

    <!--
      THE STEWARDS TRAIN THE UNITS, so the Frontier has to ask and the
      Stewards have to find the goods. Matt, 2026-09-20: "then the Stewards
      and the Frontier have to talk about what they want."
    -->
    <template v-if="picking === 'train'">
      <p ref="followUp" class="counter-band">Train what?</p>
      <ul class="hall__actions hall__actions--fit">
        <li v-for="k in unitKinds" :key="k.id">
          <button class="tile" :disabled="!k.check.ok" @click="act('train', { kind: k.id })">
            <span class="tile__head">
              <!-- eslint-disable-next-line vue/no-v-html -- constant art from frontierArt.js -->
              <svg class="tile__icon" viewBox="0 0 24 24" aria-hidden="true" v-html="k.art" />
              <span class="tile__name">{{ k.name }}</span>
            </span>
            <ActionCost :cost="k.cost" as-shifts />
            <span class="tile__text">{{ k.text }}</span>
            <span v-if="!k.check.ok && k.check.kind !== 'cost'" class="action__why">{{ k.check.why }}</span>
          </button>
        </li>
      </ul>
      <button class="secondary" @click="picking = null">Back</button>
    </template>

    <!-- Reinforce WHICH warband (playtest 2026-09-25: it asked nobody and did nothing). -->
    <template v-if="picking === 'reinforce'">
      <p ref="followUp" class="counter-band">Reinforce which warband?</p>
      <ul class="hall__actions hall__actions--fit">
        <li v-for="u in reinforceable" :key="u.id">
          <button class="tile" :disabled="!canAct" @click="act('reinforce', { unit: u.id })">
            <span class="tile__head"><span class="tile__name">{{ reinforceName(u) }}</span></span>
            <span class="tile__text">+1 strength (now {{ u.strength }})</span>
          </button>
        </li>
      </ul>
      <button class="secondary" @click="picking = null">Back</button>
    </template>

    <!-- A caravan goes SOMEWHERE now: to one of the Frontier's settlements. -->
    <template v-else-if="picking === 'caravan'">
      <p ref="followUp" class="counter-band">Send the caravan where?</p>
      <ul class="hall__actions hall__actions--fit">
        <li v-for="v in caravanTargets" :key="v.id">
          <button class="tile" :disabled="!v.check.ok" @click="act('caravan', { village: v.id })">
            <span class="tile__head"><span class="tile__name">{{ v.name }}</span></span>
            <!-- The WHOLE price for this place, food and all (playtest,
                 2026-10-15, Matt: "it's not listed as part of the price"). -->
            <ActionCost :cost="v.cost" as-shifts />
            <span class="tile__gain"><Pip :kind="v.kind" :n="v.load" signed /></span>
            <span class="tile__text">{{ v.text }}</span>
          </button>
        </li>
      </ul>
      <button class="secondary" @click="picking = null">Back</button>
    </template>

    <!-- Building is a tap on the plot you want it in. -->
    <template v-else-if="pickingBuild">
      <!-- Back is the first thing, and looks like a way out (playtest 12:
           "the back button... not very clear or obvious"). -->
      <div class="build-top">
        <button class="secondary build-top__back" @click="pickingBuild = false">‹ Back</button>
        <p ref="followUp" class="counter-band build-top__ask">
          {{ canAct ? 'Build what?' : 'What the Stewards can build' }}
          <!-- How much room the city has, said once at the top: a full city
               used to show only as a reason on each tile, below the fold
               (playtest 2026-10-20, Matt: "it's not clear visually"). -->
          <small class="build-top__room" :class="{ 'build-top__room--short': roomLeft.most < 2 }">{{ roomLeft.text }}</small>
        </p>
      </div>
      <!-- By kind, like Civilization's build list (Matt, after playtest 11):
           what you can build now, what stands already, and what a
           technology still has to unlock - with which one. -->
      <div data-tut="stewards-build-menu">
        <template v-for="g in buildGroups" :key="g.id">
          <h4 class="zone zone--flush build-group">
            <span class="zone__label">{{ g.label }}</span>
            <span v-if="g.id === 'homes'" class="zone__count zone__count--quiet">{{ civ.population }} people · {{ housing }} homes</span>
          </h4>
          <ul class="hall__actions hall__actions--fit">
            <li v-for="t in g.items" :key="t.type">
              <button class="tile" :class="{ 'tile--locked': t.locked }" :disabled="!canAct || !t.check.ok" @click="act('build', { type: t.type })">
                <span class="tile__head">
                  <!-- eslint-disable-next-line vue/no-v-html -- constant art from buildingArt.js -->
                  <svg class="tile__icon" viewBox="0 0 24 24" aria-hidden="true" v-html="t.art" />
                  <span class="tile__name">{{ t.name }}<small v-if="t.built" class="tile__built"> · {{ t.unique ? 'standing' : `${t.built} built` }}</small></span>
                </span>
                <ActionCost v-if="!t.locked && !t.done" :cost="t.cost" as-shifts />
                <!-- Can't? The ONE reason first, not a wall (playtest 9: "it gave
                     me too much information... it wasn't clear why I couldn't
                     build it", with "ask the Scholars" buried at the end). -->
                <!-- What it DOES, always (playtest 12: "what is the mill... the
                     buildings don't explain themselves"); then why not, if not. -->
                <span class="tile__text">{{ t.text.replace(/ Needs [^.]+ - ask the Scholars\./, '') }}</span>
                <span v-if="t.reason" class="action__why tile__reason">{{ t.reason }}</span>
              </button>
            </li>
          </ul>
        </template>
        <!-- The Bronze Age's verb for the Stewards: upgrade what is built. -->
        <template v-if="upgradables.length">
          <h4 class="zone zone--flush build-group"><span class="zone__label">Improve</span></h4>
          <ul class="hall__actions hall__actions--fit">
            <li v-for="u in upgradables" :key="u.id">
              <button class="tile" :disabled="!canAct || !u.check.ok" @click="act('upgrade', { building: u.id })">
                <span class="tile__head"><span class="tile__name">{{ u.from }} → {{ u.to }}</span></span>
                <ActionCost :cost="{ workers: 2, goods: 3, coin: 2 }" as-shifts />
                <span class="tile__text">a shift there makes one more</span>
              </button>
            </li>
          </ul>
        </template>
      </div>
      <button class="secondary" @click="pickingBuild = false">‹ Back</button>
    </template>

    <!--
      THE NETWORK. Four towns are four ROWS, not four boards: all the worker
      placement in this game happens on the capital's map, and everything the
      Frontier has planted is a line with what it makes. A village has no
      choices at all; a town has exactly one, a standing order it fills every
      night untouched. Matt, 2026-09-20: "they're kind of managing this
      network, but the actual worker placement happens in the main city."
    -->
    <template v-if="!picking && !pickingBuild && network.length">
      <h4 class="zone">
        <span class="zone__label">The network</span>
        <span class="zone__count zone__count--quiet">{{ network.length }}</span>
      </h4>
      <ul class="network">
        <li v-for="v in network" :key="v.id" class="net" :class="{ 'net--hurt': v.damaged }">
          <!-- Name it (playtest 9: "it'd be cool if we could rename it"). -->
          <form v-if="renaming === v.id" class="net__rename" @submit.prevent="saveName(v)">
            <input ref="renameBox" v-model="newName" class="net__input" :maxlength="24" aria-label="New name" />
            <button class="net__save" type="submit">Save</button>
          </form>
          <button v-else class="net__name net__name--edit" :disabled="readonly" :title="`Rename ${v.name}`" @click="startRename(v)">{{ v.name }} <span aria-hidden="true">✎</span></button>
          <span class="net__size">
            <span v-for="n in (v.city ? cityMax : 3)" :key="n" class="net__dot" :class="{ 'net__dot--out': n > v.size }" />
          </span>
          <span v-if="v.damaged" class="net__state">wrecked · {{ v.raids }} of {{ raidsToBurn }}</span>
          <!-- A CITY (CITY in constants.js): its focus, and its one district. -->
          <template v-else-if="v.city">
            <span class="net__orders">
              <button
                v-for="f in cityFocuses"
                :key="f"
                class="net__order"
                :class="{ 'net__order--on': v.focus === f }"
                :disabled="!canAct"
                @click="act('city-focus', { village: v.id, kind: f })"
              >{{ f }}</button>
            </span>
            <span v-if="v.district" class="net__state">city · {{ districts[v.district].name.toLowerCase() }}</span>
            <span v-else class="net__orders">
              <button
                v-for="(d, id) in districts"
                :key="id"
                class="net__order net__order--build"
                :disabled="!canAct || !districtOk(v, id)"
                :title="`${d.name}: ${d.text} (2 shifts, ${districtGoods} goods)`"
                @click="act('city-district', { village: v.id, district: id })"
              >+{{ d.name.toLowerCase() }}</button>
            </span>
          </template>
          <template v-else-if="v.chartered">
            <button v-if="raiseOk(v)" class="net__order net__order--build" :disabled="!canAct" :title="raiseTitle" @click="act('raise-city', { village: v.id })">raise a city</button>
            <span class="net__orders">
              <button
                v-for="o in orders"
                :key="o"
                class="net__order"
                :class="{ 'net__order--on': v.order === o }"
                :disabled="!canAct"
                @click="act('order', { village: v.id, kind: o })"
              >{{ o }}</button>
            </span>
          </template>
          <!-- Say when the road is in the number (playtest, 2026-10-15: Ashby
               sent +4 at size 3 and nobody could see why - it touches the
               town, which counts as road). -->
          <span v-else class="net__state">village<template v-if="v.road"> · road +{{ v.road }}</template></span>
          <Pip v-for="p in v.pips" :key="p.kind" :kind="p.kind" :n="p.n" signed />
        </li>
      </ul>
    </template>
  </div>
</template>

<script>
import { roadBonus } from '@/engine/roads';
import {
  actionsForRole, checkAction, resolveCost, resolveLabel, fieldFood as fieldFoodOf, marketSale,
  villagesOf, villageYield, caravanLoad, caravanFood,
  canReinforce, canRaiseCity, UPGRADES,
} from '@/engine/roleActions';
import { describeUnit } from '@/engine/units';
import {
  UNIT_KINDS, UNIT_ORDER, TOWN, VILLAGE, CITY,
} from '@/engine/constants';
import { artForFrontier } from './frontierArt';
import { civFor } from '@/engine/game';
import { TECHS } from '@/engine/cards';
import { missingFor, housingOf } from '@/engine/effects';
import {
  BUILDINGS, BUILDING_ORDER, BUILDING_GROUPS, cityOf, hexWithRoom, roomIn, foodCap as foodCapOf, goodsCap as goodsCapOf, ANNEX_COST,
} from '@/engine/city';
import CityScene from './CityScene.vue';
import Pip from './Pip.vue';
import { artForBuilding, artForAction } from './buildingArt';
import { layoutCity } from '@/world/cityLayout';

// What a shift in each building brings back, so a label can say +2 WHAT.
const YIELD_KIND = { field: 'food', workshop: 'goods', market: 'coin' };
// The engine counts everybody's people in "workers"; the Stewards' are shifts,
// and one of them is a shift, not "1 more shifts".
// What each unit is for, in the fewest words that will do.
const UNIT_TEXT = {
  warband: 'fights, and holds a hex against raiders',
  scout: 'two hexes an order, sees further, cannot fight',
  pioneer: 'founds a village, and is spent doing it',
};
const ART_FOR = { warband: 'fight', scout: 'scout', pioneer: 'outpost' };
const shiftWords = (why) => String(why || '')
  .replace(/\bworkers\b/g, 'shifts')
  .replace(/\bworker\b/g, 'shift');
import ActionCost from './ActionCost.vue';

// The Stewards' board: the city map, and what to do with the hands.
// Working a building is a tap on it; annexing is a tap on dashed land;
// building picks a type. Off-turn it is the same screen, inert.
export default {
  name: 'StewardsBoard',
  components: { ActionCost, CityScene, Pip },
  props: {
    game: { type: Object, required: true },
    readonly: { type: Boolean, default: false },
    preview: { type: Boolean, default: false },
  },
  emits: ['act', 'undo'],
  data() {
    return {
      pickingBuild: false, picking: null, flash: null, annexing: null, expanding: false,
      renaming: null, newName: '',
    };
  },
  computed: {
    civ() { return civFor(this.game, 'trade'); },
    canAct() { return !this.readonly && !this.preview; },
    city() { return cityOf(this.civ); },
    foodCap() { return foodCapOf(this.civ); },
    goodsCap() { return goodsCapOf(this.civ); },
    ANNEX_COST() { return ANNEX_COST; },
    annexArt() { return artForAction('annex'); },
    buildArt() { return artForBuilding('workshop'); },
    annexCheck() {
      const first = this.cityScene.annex[0];
      return first
        ? checkAction(this.game, 'trade', 'annex', { hex: { q: first.q, r: first.r } })
        : { ok: false, why: 'Nowhere to expand.' };
    },
    annexWhy() { return shiftWords(this.annexCheck.why); },
    reinforceable() { return (this.civ.units || []).filter((u) => canReinforce(this.civ, u)); },
    roomLeft() {
      const city = cityOf(this.civ);
      const free = city.hexes.map((h) => roomIn(city, h));
      const total = free.reduce((n, x) => n + x, 0);
      const most = Math.max(0, ...free);
      if (!total) return { most, text: 'The city is full - take in land to build' };
      if (most < 2) return { most, text: `${total} plot${total === 1 ? '' : 's'} free, none with room for a two-plot building - take in land` };
      return { most, text: `${total} plots free in the city` };
    },
    builtToday() { return checkAction(this.game, 'trade', 'build', { type: BUILDING_ORDER[0] }).kind === 'taken'; },
    roomAnywhere() { return BUILDING_ORDER.some((t) => Boolean(hexWithRoom(cityOf(this.civ), t))); },
    buildAnywhere() { return BUILDING_ORDER.some((t) => checkAction(this.game, 'trade', 'build', { type: t }).ok); },
    yields() {
      return { field: fieldFoodOf(this.civ), workshop: 2, market: marketSale(this.civ).coin };
    },
    // What hangs over the board: what each building is, what a shift there
    // brings back, and - once you have picked one - what a piece of land
    // would cost to take in.
    labels() { return this.labelsFor(this.cityScene); },
    // The city, laid out for the 3D board.
    cityScene() { return layoutCity(this.civ, this.yields); },
    buildOptions() {
      return BUILDING_ORDER.map((type) => {
        const b = BUILDINGS[type];
        const built = cityOf(this.civ).buildings.filter((x) => x.type === type).length;
        return {
          type,
          ...b,
          built,
          locked: Boolean(b.needsTech) && !(this.civ.techs || []).includes(b.needsTech),
          done: Boolean(b.unique) && built > 0,
          art: artForBuilding(type),
          cost: { workers: 2, ...b.cost },
          check: checkAction(this.game, 'trade', 'build', { type }),
          reason: this.buildReason(type),
        };
      });
    },
    // The menu's shelves; a group with nothing in it is left out.
    buildGroups() {
      return BUILDING_GROUPS.map((g) => ({ ...g, items: this.buildOptions.filter((t) => (t.group || 'work') === g.id) }))
        .filter((g) => g.items.length);
    },
    housing() { return housingOf(this.civ); },
    // What the Frontier has planted, as rows. A village has no choices; a
    // town has one, the standing order it fills every night.
    network() {
      return villagesOf(this.civ).map((v) => {
        const made = villageYield(this.civ, v);
        // EVERY thing it makes: a city with a market district makes its
        // focus AND a coin, and the row used to show only the first - "+1
        // coin" for a knowledge city (playtest 2026-10-20, Ashby).
        return {
          ...v,
          pips: Object.keys(made).filter((k) => made[k]).map((k) => ({ kind: k, n: made[k] })),
          road: v.damaged || v.chartered ? 0 : roadBonus(this.civ, v),
        };
      });
    },
    upgradables() {
      if ((this.civ.age || 0) < 1) return [];
      return cityOf(this.civ).buildings.filter((b) => UPGRADES[b.type] && !b.upgraded && !b.burned)
        .map((b) => ({ id: b.id, from: BUILDINGS[b.type].name, to: UPGRADES[b.type], check: checkAction(this.game, 'trade', 'upgrade', { building: b.id }) }));
    },
    orders() { return TOWN.orders; },
    cityMax() { return CITY.maxSize; },
    cityFocuses() { return CITY.focuses; },
    districts() { return CITY.districts; },
    districtGoods() { return CITY.districtCost.goods; },
    raiseTitle() { return `Raise a city: 2 shifts, ${CITY.cost.goods} goods, ${CITY.cost.coin} coin, and ${CITY.settlers} people leave the capital to settle it`; },
    raidsToBurn() { return VILLAGE.raidsToBurn; },
    unitKinds() {
      return UNIT_ORDER.map((id) => ({
        id,
        name: UNIT_KINDS[id].name,
        cost: { workers: 2, ...UNIT_KINDS[id].trainCost },
        art: artForFrontier(ART_FOR[id]),
        text: UNIT_TEXT[id],
        // The greyed next thing names what it needs (Matt, playtest 13).
        check: id === 'scout' && !(this.civ.techs || []).includes('tracking')
          ? { ok: false, kind: 'rule', why: 'Needs Tracking - ask the Scholars.' }
          : checkAction(this.game, 'trade', 'train', { kind: id }),
      }));
    },
    caravanTileCost() {
      return (a) => villagesOf(this.civ).filter((v) => !v.damaged)
        .map((v) => resolveCost(a, this.civ, 1, { village: v.id }, this.game))
        .reduce((best, c) => ((c.food || 0) > (best.food || 0) ? c : best), resolveCost(a, this.civ));
    },
    caravanTargets() {
      const caravan = actionsForRole('trade').find((a) => a.id === 'caravan');
      return villagesOf(this.civ).filter((v) => !v.damaged).map((v) => {
        const made = villageYield(this.civ, v);
        return {
          id: v.id,
          name: v.name,
          kind: Object.keys(made)[0] || 'goods',
          load: caravanLoad(this.civ, v),
          // Fed to grow it costs food; full-grown, or a town, it does not.
          text: caravanFood(this.civ, v) ? `${caravanFood(this.civ, v)} food out, and ${v.name} grows` : (v.chartered ? 'a town does not grow by caravan' : 'already as big as it gets'),
          cost: caravan ? resolveCost(caravan, this.civ, 1, { village: v.id }, this.game) : {},
          check: checkAction(this.game, 'trade', 'caravan', { village: v.id }),
        };
      });
    },
    buttons() {
      const { civ } = this;
      return actionsForRole('trade')
        // Working, building, annexing and repairing happen on the map. The
        // rest are errands out of town, and they are buttons.
        .filter((a) => !['work', 'annex', 'repair', 'build', 'order'].includes(a.id)) // a town's order is set in its network row
        .filter((a) => a.id !== 'build-monument' || civ.monument.revealed)
        .map((a) => ({
          id: a.id,
          label: resolveLabel(a, civ),
          gain: a.gain(civ),
          blurb: a.blurb,
          // A caravan's price depends on where it goes; the tile shows the
          // dearest (food and all), so the food is never a surprise
          // (playtest, 2026-10-15: "it's not listed as part of the price").
          cost: a.id === 'caravan' ? this.caravanTileCost(a) : resolveCost(a, civ),
          art: artForAction(a.id),
          check: { ...checkAction(this.game, 'trade', a.id), why: shiftWords(checkAction(this.game, 'trade', a.id).why) },
        }))
        // House style rule 4, the same rule the Frontier's tiles follow:
        // only what is POSSIBLE here. A thing the rules forbid outright is
        // not listed; a thing you merely cannot afford stays, greyed, with
        // its price already on the tile.
        .filter((a) => a.check.ok || a.check.kind === 'cost');
    },
  },
  watch: {
    // Playtest 7: "I tap train a unit, and then it says train what? But the
    // page should scroll down a little bit to show that it expanded."
    picking(now) {
      if (!now) return;
      this.$nextTick(() => {
        const el = [].concat(this.$refs.followUp || []).find(Boolean);
        el?.scrollIntoView?.({ behavior: 'smooth', block: 'start' });
      });
    },
  },
  methods: {
    raiseOk(v) { return canRaiseCity(this.civ, v); },
    districtOk(v, id) { return checkAction(this.game, 'trade', 'city-district', { village: v.id, district: id }).ok; },
    startRename(v) {
      this.renaming = v.id;
      this.newName = v.name;
      this.$nextTick(() => { const box = [].concat(this.$refs.renameBox || [])[0]; box?.focus?.(); box?.select?.(); });
    },
    saveName(v) {
      const name = this.newName.trim();
      if (name && name !== v.name) this.$store.dispatch('renameVillage', { village: v.id, name });
      this.renaming = null;
    },
    buildReason(type) {
      const b = BUILDINGS[type];
      // Once a day: say so, or a menu with the room and the goods reads as
      // broken (playtest 2026-10-20, Matt: "I need to understand why").
      if (this.builtToday) return 'One building a day - you have built today.';
      if (b.unique && cityOf(this.civ).buildings.some((x) => x.type === type)) return 'Already standing - one is all the town needs.';
      if (b.needsTech && !(this.civ.techs || []).includes(b.needsTech)) return `Needs ${TECHS[b.needsTech]?.name || b.needsTech} - ask the Scholars.`;
      if (!hexWithRoom(cityOf(this.civ), type)) return 'No room for it - take in land first.';
      // Say WHAT is short (playtest 12, Matt: "not enough in the stores yet
      // - I don't know what that means").
      const short = missingFor(this.civ, { workers: 2, ...(b.cost || {}) }).map((x) => x.replace(/more workers?$/, (m) => (m.endsWith('s') ? 'more shifts' : 'more shift')));
      if (short.length) return `Needs ${short.join(' and ')}.`;
      return '';
    },
    labelsFor(city) {
      // A number on its own says nothing: the pip says +2 WHAT.
      // Anchored to the worker's pad, in FRONT of the building, and it says
      // which building it is - "I don't know what these buildings are."
      // Houses go unlabelled: nothing to staff, and three of them at the
      // start crowded the names that matter (housing, 2026-10-15). The
      // homes count lives in the Build menu and on the TV. A burned one
      // still says so.
      const out = city.buildings.filter((b) => b.type !== 'house' || b.burned).map((b) => ({
        id: b.id,
        kind: 'building',
        name: BUILDINGS[b.type]?.name || b.type,
        pip: b.burned || !b.yield ? null : { kind: YIELD_KIND[b.type], n: b.yield },
        // The market is a TRADE, so its label says what it takes too
        // (playtest 7: "it just cost me 2 goods to get 3 coins... I had no
        // idea that was what was going to happen").
        pay: b.type === 'market' && !b.burned ? { kind: 'goods', n: marketSale(this.civ).goods } : null,
        burned: b.burned,
        slots: b.slots,
        staffed: b.staffed,
        ready: b.workable,
      }));
      // Only the piece of land you have actually picked says what it costs.
      // Labelling all nine at once buried the city.
      const pick = this.annexing;
      if (pick) {
        const a = city.annex.find((h) => h.q === pick.q && h.r === pick.r);
        const check = checkAction(this.game, 'trade', 'annex', { hex: pick });
        if (a) {
          out.push({
            id: a.id,
            kind: 'annex',
            q: a.q,
            r: a.r,
            cost: ANNEX_COST,
            ready: check.ok,
            why: check.ok ? 'tap again to take it' : shiftWords(check.why),
          });
        }
      }
      return out;
    },
    onPick(hit) {
      if (!this.canAct) return;
      if (hit.kind === 'building') { this.onBuilding(hit.id); return; }
      if (hit.kind === 'plot') { this.pickingBuild = true; return; }
      // Taking in land is two taps: the first picks the piece and shows what
      // it costs, the second does it. It should not happen by brushing past.
      if (hit.kind === 'annex') {
        const same = this.annexing && this.annexing.q === hit.q && this.annexing.r === hit.r;
        if (!same) { this.annexing = { q: hit.q, r: hit.r }; return; }
        this.onAnnex({ q: hit.q, r: hit.r });
      }
    },
    onBuilding(id) {
      if (!this.canAct) return;
      const b = this.city.buildings.find((x) => x.id === id);
      if (!b) return;
      if (b.burned) {
        const c = checkAction(this.game, 'trade', 'repair', { building: id });
        if (c.ok) {
          this.say(id, 'repaired');
          this.act('repair', { building: id });
        } else {
          // Say why, where it happened (playtest 2026-09-25: the tap did
          // nothing and "I don't know how to fix the burned fields").
          this.say(id, shiftWords(c.why || 'cannot repair yet'));
        }
        return;
      }
      if (!checkAction(this.game, 'trade', 'work', { building: id }).ok) return;
      // Say what the shift just brought back, where it happened.
      const kind = YIELD_KIND[b.type];
      const n = this.yields[b.type];
      this.say(id, kind && n ? `+${n} ${kind}` : 'at work');
      this.act('work', { building: id });
    },
    say(id, text) {
      this.flash = { id, text };
      window.setTimeout(() => { this.flash = null; }, 1200);
    },
    onAnnex({ q, r }) {
      if (!this.canAct) return;
      if (checkAction(this.game, 'trade', 'annex', { hex: { q, r } }).ok) this.act('annex', { hex: { q, r } });
    },
    // Two actions ask a question first, the way Build already does.
    tap(a) {
      if (a.id === 'train') { this.picking = 'train'; return; }
      if (a.id === 'caravan') { this.picking = 'caravan'; return; }
      if (a.id === 'reinforce') {
        const list = this.reinforceable;
        if (list.length === 1) { this.act('reinforce', { unit: list[0].id }); return; }
        if (list.length > 1) { this.picking = 'reinforce'; return; }
      }
      this.act(a.id, {});
    },
    reinforceName(u) { return describeUnit(u); },
    act(actionId, params = {}) {
      this.picking = null;
      this.pickingBuild = false;
      this.annexing = null;
      this.expanding = false;
      this.$emit('act', { role: 'trade', actionId, params });
    },
  },
};
</script>
